import { css, cx } from "@emotion/css";
import type { ErrorInfo, SimpleMenuItem } from "@octopusdeploy/design-system-components";
import { EmptyStateNoResults, ErrorPanel, DeprecatedIconButton, OverflowMenu, SearchInput, Tooltip } from "@octopusdeploy/design-system-components";
import { ArrowRefreshIcon } from "@octopusdeploy/design-system-icons";
import { DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { borderWidth, fontFamilyGlobal, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { Moment } from "moment";
import { useCallback, useMemo, useState } from "react";
import { Skeleton } from "~/components/Skeleton/Skeleton";
import TimeFromNowLabel from "~/components/TimeLabels/TimeFromNowLabel";
import useLocalStorage from "~/hooks/useLocalStorage";
type KubernetesPodLogLine = {
    Message: string;
    Timestamp: string;
};
type TableBackgroundColor = "primary" | "secondary";
const styles = {
    flexRowWithGap: css({
        display: "flex",
        flexDirection: "row",
        gap: space[16],
        alignItems: "center",
    }),
    spaceBetween: css({
        justifyContent: "space-between",
    }),
    header: css({
        display: "flex",
        flexDirection: "row",
        gap: space[16],
        alignItems: "center",
        paddingLeft: space[16],
        paddingRight: space[16],
        backgroundColor: themeTokens.color.background.primary.default,
    }),
    logsTableContainer: css({
        height: "100%",
        overflowY: "auto",
    }),
    logsTable: css({
        width: "100%",
        borderSpacing: 0,
        tableLayout: "auto",
    }),
    td: css({
        padding: `${space[4]} ${space[8]}`,
        font: text.body.regular.medium,
        color: themeTokens.color.text.primary,
        ":first-of-type": {
            paddingLeft: space[16],
        },
        ":last-of-type": {
            paddingRight: space[16],
        },
    }),
    calloutTd: css({
        padding: `${space[8]} ${space[16]}`,
        font: text.body.regular.small,
        color: themeTokens.color.text.secondary,
    }),
    topCalloutTd: css({
        borderBottom: `${borderWidth["1"]} solid ${themeTokens.color.border.primary}`,
    }),
    bottomCalloutTd: css({
        borderTop: `${borderWidth["1"]} solid ${themeTokens.color.border.primary}`,
    }),
    logTr: (background: TableBackgroundColor) => css({
        backgroundColor: background === "primary" ? themeTokens.color.background.primary.default : themeTokens.color.background.secondary.default,
        ":hover": {
            backgroundColor: themeTokens.color.background.tertiary,
        },
    }),
    logTd: css({
        fontFamily: fontFamilyGlobal["system-ui-monospace"],
    }),
    messageTd: (enableWordWrap: boolean) => css({
        verticalAlign: "top",
        whiteSpace: enableWordWrap ? "pre-line" : "nowrap",
        //the tableLayout: auto is forgiving so this forces this cell to expand to fill the available space
        width: "100%",
    }),
    timestampTd: css({
        verticalAlign: "top",
        color: themeTokens.color.text.primary,
        whiteSpace: "nowrap",
    }),
    loadingTd: css({
        height: `calc(20px + (${space[4]} * 2))`,
        ":first-of-type": {
            width: `calc(15 * ${space[16]})`,
        },
    }),
    refreshLink: css({
        cursor: "pointer",
    }),
    footerContainer: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
    }),
};
type KubernetesPodLogsProps = {
    logLines: KubernetesPodLogLine[] | null;
    isLoading: boolean;
    errorInfo: ErrorInfo | null;
    lastRefreshed: Moment;
    refresh: () => void;
    localStoragePrefix: string;
    onPreviousContainerChanged?: (enabled: boolean) => void;
    pods?: {
        Id: string;
        Name: string;
    }[];
    selectedPod?: string;
    onSelectedPodChanged?: (id: string) => void;
    containers?: string[];
    selectedContainer?: string;
    onSelectedContainerChanged?: (containerName: string) => void;
    backgroundColor?: TableBackgroundColor;
};
export const KubernetesPodLogs = (props: KubernetesPodLogsProps) => {
    const { logLines, isLoading, errorInfo, lastRefreshed, refresh, localStoragePrefix, onPreviousContainerChanged, pods, selectedPod, onSelectedPodChanged, containers, selectedContainer, onSelectedContainerChanged, backgroundColor } = props;
    const [searchFilter, setSearchFilter] = useState<string | undefined>();
    const [showTimestampsInStorage, setShowTimestampsInStorage] = useLocalStorage<boolean>(`${localStoragePrefix}.ShowTimestampsInLogs`, false);
    const [showTimestamps, setShowTimestamps] = useState<boolean>(showTimestampsInStorage);
    const [enableWordWrapInStorage, setEnableWordWrapInStorage] = useLocalStorage<boolean>(`${localStoragePrefix}.ShowWordWrapInLogs`, false);
    const [enableWordWrap, setEnableWordWrap] = useState<boolean>(enableWordWrapInStorage);
    const [showPreviousContainer, setShowPreviousContainer] = useState<boolean>(false);
    const hasPodOrContainerFiltering = !!pods || !!containers;
    const updatePod = (pod: string | undefined) => {
        if (!pod) {
            throw new Error("No pod selected");
        }
        //clear the search field when the container changes
        setSearchFilter(undefined);
        onSelectedPodChanged?.(pod);
    };
    const updateContainer = (container: string | undefined) => {
        if (!container) {
            throw new Error("No container selected");
        }
        //clear the search field when the container changes
        setSearchFilter(undefined);
        onSelectedContainerChanged?.(container);
    };
    const toggleShowTimestamps = useCallback((enabled: boolean) => {
        setShowTimestamps(enabled);
        setShowTimestampsInStorage(enabled);
    }, [setShowTimestampsInStorage]);
    const toggleEnableWordWrap = useCallback((enabled: boolean) => {
        setEnableWordWrap(enabled);
        setEnableWordWrapInStorage(enabled);
    }, [setEnableWordWrapInStorage]);
    const filteredLogs = useMemo(() => {
        if (!searchFilter) {
            return logLines;
        }
        const lowerCaseSearch = searchFilter.toLocaleLowerCase();
        return logLines?.filter((l) => {
            const inMsg = l.Message.toLocaleLowerCase().includes(lowerCaseSearch);
            if (inMsg) {
                return true;
            }
            //if the timestamps are visible, include those values in the filter
            return showTimestamps ? l.Timestamp.toLocaleLowerCase().includes(lowerCaseSearch) : false;
        });
    }, [searchFilter, logLines, showTimestamps]);
    const totalCols = showTimestamps ? 2 : 1;
    const overflowMenuItems: SimpleMenuItem[] = useMemo(() => {
        let items: SimpleMenuItem[] = [
            { type: "toggle", label: "Show timestamps", isEnabled: showTimestamps, setIsEnabled: (enabled) => toggleShowTimestamps(enabled) },
            { type: "toggle", label: "Wrap lines", isEnabled: enableWordWrap, setIsEnabled: (enabled) => toggleEnableWordWrap(enabled) },
        ];
        if (onPreviousContainerChanged) {
            items = [
                {
                    type: "toggle",
                    label: "Show previous container",
                    isEnabled: showPreviousContainer,
                    setIsEnabled: (enabled) => {
                        setShowPreviousContainer(enabled);
                        onPreviousContainerChanged(enabled);
                    },
                },
                ...items,
            ];
        }
        return items;
    }, [enableWordWrap, onPreviousContainerChanged, showPreviousContainer, showTimestamps, toggleEnableWordWrap, toggleShowTimestamps]);
    return (<>
            <div className={cx(styles.flexRowWithGap, styles.header, !hasPodOrContainerFiltering ? styles.spaceBetween : undefined)}>
                <SearchInput fullWidth={hasPodOrContainerFiltering} value={searchFilter || ""} onChange={(val) => setSearchFilter(val)} accessibleName={"Search pod logs"} placeholder={"Search"}/>
                {pods && <DeprecatedSelect label="Pod" value={selectedPod} onChange={(val) => updatePod(val)} items={pods.map((pod) => ({ value: pod.Id, text: pod.Name }))} allowClear={false}/>}
                {containers && <DeprecatedSelect label="Container" value={selectedContainer} onChange={(val) => updateContainer(val)} items={containers.map((x) => ({ value: x, text: x }))} allowClear={false}/>}
                <div className={styles.flexRowWithGap}>
                    <Tooltip content={"Refresh"}>
                        <DeprecatedIconButton customIcon={<ArrowRefreshIcon size={20}/>} onClick={() => refresh()}/>
                    </Tooltip>
                    <OverflowMenu menuItems={overflowMenuItems}/>
                </div>
            </div>
            <div className={styles.logsTableContainer}>
                {errorInfo && <ErrorPanel error={errorInfo}/>}
                <table className={styles.logsTable}>
                    <colgroup>
                        {showTimestamps && <col />}
                        <col />
                    </colgroup>
                    <tbody>
                        {isLoading &&
            Array.from({ length: 5 }, (_, i) => (<tr key={`loadingSkeleton-${i}`}>
                                    {showTimestamps && (<td className={cx(styles.td, styles.logTd, styles.loadingTd)}>
                                            <Skeleton shape={"Rounded"} borderRadius={"small"}/>
                                        </td>)}
                                    <td className={cx(styles.td, styles.logTd, styles.loadingTd)}>
                                        <Skeleton shape={"Rounded"} borderRadius={"small"}/>
                                    </td>
                                </tr>))}
                        {!isLoading &&
            filteredLogs &&
            (filteredLogs.length > 0 ? (<>
                                    <tr>
                                        <td className={cx(styles.calloutTd, styles.topCalloutTd)} colSpan={totalCols}>
                                            You've reached the oldest available logs.
                                        </td>
                                    </tr>
                                    {filteredLogs.map((l, i) => (<tr key={`${i}-${l.Timestamp}`} className={styles.logTr(backgroundColor || "secondary")}>
                                            {showTimestamps && <td className={cx(styles.td, styles.logTd, styles.timestampTd)}>{l.Timestamp}</td>}
                                            <td className={cx(styles.td, styles.logTd, styles.messageTd(enableWordWrap))}>{l.Message}</td>
                                        </tr>))}
                                    <tr>
                                        <td className={cx(styles.calloutTd, styles.bottomCalloutTd)} colSpan={totalCols}>
                                            <div className={styles.footerContainer}>
                                                <span>You've reached the latest available logs.</span>
                                                <div>
                                                    <span>
                                                        Last refreshed <TimeFromNowLabel time={lastRefreshed}/>.
                                                    </span>{" "}
                                                    <a className={styles.refreshLink} onClick={() => refresh()}>
                                                        Refresh to check for updates.
                                                    </a>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </>) : (<tr>
                                    <td className={styles.td} colSpan={totalCols}>
                                        <EmptyStateNoResults />
                                    </td>
                                </tr>))}
                    </tbody>
                </table>
            </div>
        </>);
};
